import {
  Box,
  Button,
  Divider,
  Stack,
  type SxProps,
  styled,
  type Theme,
  Typography,
} from '@mui/material';
import {
  Alert,
  CardSection,
  CardSubSection,
  type AlertProps as PantryAlertProps,
  Show,
} from '@side/pantry';
import { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AddressInput } from 'src/components/AddressInput';
import { addressAutofillHelper } from 'src/components/AddressInput/StreetAutocomplete/addressAutofillHelper';
import { useLicensedStateContext } from 'src/contexts/LicensedStateContext';
import type { GooglePlacesResult } from 'src/views/Coversheet/utils/googlePlaces';
import { CancelButton } from 'src/views/NewInteraction/CancelButton';
import { MLSForm } from 'src/views/NewInteraction/MLSForm';
import { useInteraction } from '@/common/hooks/useInteraction';
import type { InteractionType } from '@/common/types/interaction';
import { ListingSummaryCardRenderer } from '@/components/ListingSummaryCard/ListingSummaryCardRenderer';
import { useListingSummary } from '@/components/ListingSummaryCard/useListingSummary';
import { useAppFlags } from '$launch-darkly/app-flags';
import { ListingConnectSwap } from './ListingConnectSwap';
import { MLSReadOnlyCard } from './MLSReadOnlyCard';
import { StatusSelect } from './StatusSelect';
import type { ErrorMessage } from './types';

const MLS_TRANSITION_OFFSET_MS = 100;

const ButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  paddingBottom: theme.spacing(7),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
}));

interface Props {
  sourceId?: string;
  type: InteractionType;
  handleSetErrorMessage: (error: ErrorMessage) => void;
  interactionId?: string;
  headerText: string;
  subheaderText?: string;
  incomplete?: boolean;
  enableMlsAutofill?: boolean;
  enableListingMatch?: boolean;
  onListingMatch?: (
    listingId: string,
    runAutofill: () => Promise<void>,
  ) => void;
  submitButtonText?: string;
  sx?: SxProps<Theme>;
  stateOptions?: string[];
  AlertProps?: PantryAlertProps;
}

export const AddressForm = ({
  sourceId,
  type,
  handleSetErrorMessage,
  interactionId,
  incomplete,
  headerText,
  subheaderText,
  enableMlsAutofill,
  enableListingMatch,
  onListingMatch,
  submitButtonText,
  sx,
  stateOptions,
  AlertProps,
}: Props) => {
  const isCloneUrl = !!sourceId;
  const {
    setValue,
    trigger,
    formState: { isValid, isSubmitting },
  } = useFormContext();
  const handleAddressAutofill = useCallback(
    (autofillValue: GooglePlacesResult) => {
      addressAutofillHelper(autofillValue, setValue);
    },
    [setValue],
  );

  const { txmLinkOffersListings } = useAppFlags();
  const listingId = useWatch<{ listingId?: string }, 'listingId'>({
    name: 'listingId',
  });
  const readOnlyEnabled =
    !!enableListingMatch && !!txmLinkOffersListings && !!listingId;

  const { listing, loading: listingLoading } = useListingSummary({ listingId });

  // Read-only unmounts the inputs that drive validation, so re-run it on entry;
  // otherwise `isValid` goes stale and the submit button stays disabled
  // after navigating back to this step.
  useEffect(() => {
    if (readOnlyEnabled) {
      void trigger();
    }
  }, [readOnlyEnabled, trigger]);

  const { data: sourceInteraction, error: errorGetTransactionPropertyAddress } =
    useInteraction(sourceId, type, !isCloneUrl, {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    });
  const {
    stateError,
    statesWithSubmitPermission,
    licensesLoading,
    redirectStateError,
  } = useLicensedStateContext();

  useEffect(() => {
    if (!licensesLoading && statesWithSubmitPermission.length < 1) {
      handleSetErrorMessage('errorLoadingPage');
    } else if (errorGetTransactionPropertyAddress) {
      handleSetErrorMessage('errorCloneTransaction');
    }
  }, [
    handleSetErrorMessage,
    licensesLoading,
    statesWithSubmitPermission,
    errorGetTransactionPropertyAddress,
  ]);

  const clonedPropertyLocation = sourceInteraction?.property;

  useEffect(() => {
    if (!clonedPropertyLocation) {
      return;
    }

    if (clonedPropertyLocation.mlsServiceId) {
      setValue('mlsVendor', clonedPropertyLocation.mlsServiceId);
    }

    // Narrowed to the address fields because `addressAutofillHelper` writes
    // every key it receives into the form.
    handleAddressAutofill({
      street1: clonedPropertyLocation.street1,
      street2: clonedPropertyLocation.street2,
      city: clonedPropertyLocation.city,
      state: clonedPropertyLocation.state,
      zip: clonedPropertyLocation.zip,
      county: clonedPropertyLocation.county,
      mlsNumber: clonedPropertyLocation.mlsNumber,
      mlsServiceId: clonedPropertyLocation.mlsServiceId,
    });
  }, [clonedPropertyLocation, handleAddressAutofill, setValue]);

  return (
    <Stack sx={{ gap: 4, ...sx }}>
      <Show when={enableMlsAutofill}>
        <ListingConnectSwap
          connected={readOnlyEnabled}
          enterDelay={MLS_TRANSITION_OFFSET_MS}
          editable={
            <MLSForm
              type={type}
              handleAddressAutofill={handleAddressAutofill}
              onListingMatch={onListingMatch}
            />
          }
          readOnly={
            <MLSReadOnlyCard listing={listing} loading={listingLoading} />
          }
        />
        <Divider>
          <Typography variant="subtitle2">OR</Typography>
        </Divider>
      </Show>

      <CardSection cardTitle={headerText} data-testid="address-form">
        <ListingConnectSwap
          connected={readOnlyEnabled}
          editable={
            <CardSubSection sx={{ py: 3 }}>
              {subheaderText ? (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', mb: 1 }}
                >
                  {subheaderText}
                </Typography>
              ) : null}
              {!!redirectStateError && (
                <Alert sx={{ my: 2 }} severity="error">
                  {redirectStateError}
                </Alert>
              )}
              <AddressInput
                stateOptions={stateOptions ?? statesWithSubmitPermission}
                onValueChange={setValue}
                interactionId={interactionId ?? ''}
                type={type}
                incomplete={incomplete}
                enableListingMatch={enableListingMatch}
              />
              <Show when={AlertProps}>
                <Alert {...AlertProps} sx={{ mt: 1.75, ...AlertProps?.sx }} />
              </Show>
            </CardSubSection>
          }
          readOnly={
            <CardSubSection sx={{ py: 3 }}>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mb: 3 }}
              >
                Property and seller details will autofill from this listing
              </Typography>
              <ListingSummaryCardRenderer
                listing={listing}
                loading={listingLoading}
                listingId={listingId}
              />
            </CardSubSection>
          }
        />
      </CardSection>

      <StatusSelect
        type={type}
        sourceInteractionId={sourceId}
        setErrorMessage={handleSetErrorMessage}
      />

      {submitButtonText && (
        <ButtonWrapper>
          <CancelButton type={type} />
          <Button
            type="submit"
            size="large"
            disabled={!isValid || isSubmitting || stateError.length > 0}
            loading={isSubmitting}
            variant="contained"
          >
            {submitButtonText}
          </Button>
        </ButtonWrapper>
      )}
    </Stack>
  );
};
